import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import KeyNameArrayInput, { type KeyNameItem } from '@/components/ui/KeyNameArrayInput'
import { designTokens } from '@/utils/design-tokens'

interface BusinessCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (businessData: {
    name: string
    sortKey?: number
    labels?: Array<{key: string; name: string}>
    detail_fields?: Array<{key: string; name: string}>
    extra_fields?: Array<{key: string; name: string}>
  }) => void
  isLoading?: boolean
}

export default function BusinessCreateModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}: BusinessCreateModalProps) {
  const [businessName, setBusinessName] = useState('')
  const [sortKey, setSortKey] = useState<string>('')
  const [labels, setLabels] = useState<KeyNameItem[]>([])
  const [detailFields, setDetailFields] = useState<KeyNameItem[]>([])
  const [extraFields, setExtraFields] = useState<KeyNameItem[]>([])

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setBusinessName('')
      setSortKey('')
      setLabels([])
      setDetailFields([])
      setExtraFields([])
    }
  }, [isOpen])

  const handleSubmit = () => {
    if (businessName.trim()) {
      const sortKeyNumber = sortKey.trim() ? parseInt(sortKey, 10) : undefined
      onSubmit({
        name: businessName.trim(),
        sortKey: !isNaN(sortKeyNumber as number) ? sortKeyNumber : undefined,
        labels: labels.length > 0 ? labels : undefined,
        detail_fields: detailFields.length > 0 ? detailFields : undefined,
        extra_fields: extraFields.length > 0 ? extraFields : undefined
      })
    }
  }

  const handleClose = () => {
    setBusinessName('')
    setSortKey('')
    setLabels([])
    setDetailFields([])
    setExtraFields([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              사업 생성
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Form Section */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-[10px]">
                {/* Business Name Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        사업명
                        <span
                          className="ml-1"
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: designTokens.typography.fontSize['body-xsmall'][0],
                            lineHeight: designTokens.typography.fontSize['body-xsmall'][1].lineHeight,
                            color: designTokens.colors.error.DEFAULT
                          }}
                        >
                          *
                        </span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="사업명을 입력해주세요."
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        size="small"
                        className="w-full"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>

                {/* Sort Key Input */}
                <div className="flex flex-col gap-[10px]">
                  <div className="flex items-center gap-5">
                    <div className="w-[150px]">
                      <label
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: designTokens.typography.fontSize['body-medium'][0],
                          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                          fontWeight: designTokens.typography.fontSize['body-medium'][1].fontWeight,
                          color: designTokens.colors.gray[6]
                        }}
                      >
                        순서
                      </label>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        placeholder="순서를 입력해주세요. (선택사항)"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        size="small"
                        className="w-full"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Labels Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="라벨"
                    values={labels}
                    onChange={setLabels}
                    keyPlaceholder="라벨 키를 입력해주세요"
                    disabled={isLoading}
                    keyOnly={true}
                  />
                </div>

                {/* Detail Fields Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="상세 필드"
                    values={detailFields}
                    onChange={setDetailFields}
                    keyPlaceholder="필드 키를 입력해주세요"
                    namePlaceholder="필드 이름을 입력해주세요"
                    disabled={isLoading}
                  />
                </div>

                {/* Extra Fields Input */}
                <div className="flex flex-col gap-[10px]">
                  <KeyNameArrayInput
                    label="추가 필드"
                    values={extraFields}
                    onChange={setExtraFields}
                    keyPlaceholder="필드 키를 입력해주세요"
                    namePlaceholder="필드 이름을 입력해주세요"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-[10px]">
              <Button
                color="light"
                size="medium"
                onClick={handleClose}
                disabled={isLoading}
                className="px-[10px] h-10"
              >
                취소
              </Button>
              <Button
                color="dark"
                size="medium"
                onClick={handleSubmit}
                disabled={isLoading || !businessName.trim()}
                className="px-[10px] h-10"
              >
                {isLoading ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}